import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Result,
	Row,
	Select,
	Space,
	Table,
	Upload,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getFRMVisitReportDetail,
	submitFRMRejectVisitReport,
} from '@/libs/fleet-api/services/visit-reports';
import MainInformation from '@/components/MainInformation';
import FleetModal from '@/libs/antd/components/Modal';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import { useApp } from '@/App';
import { useSelectRejectCategory } from '@/libs/fleet-api/hooks/master-data';
import rule from '@/libs/antd/rules';
import { uploader } from '@/libs/fleet-api/uploader';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import {
	FilePayload,
	RejectVisitReport,
} from '@/libs/fleet-api/services/types';
import fetcher from '@/libs/fleet-api/fetcher';
import { FileData } from '@/libs/fleet-api/services/customer';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type FRMVisitReportRejectPageProps = {
	prospectId: string;
};

type RejectVisitReportForm = {
	input?: RejectVisitReport & { photoData: FileData };
};

const FRMVisitReportRejectPage: FC<FRMVisitReportRejectPageProps> = ({
	prospectId,
}) => {
	const { navigate, notification } = useApp();
	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getFRMVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getFRMVisitReportDetail(id);
		},
		enabled: !!prospectId,
	});
	const [showModal, setShowModal] = useState<boolean>(false);
	const { form, fieldName } = useForm<RejectVisitReportForm>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const rejectMutation = useMutation({
		mutationFn: submitFRMRejectVisitReport,
	});

	const selectRejectCategory = useSelectRejectCategory();

	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		approvalHistories,
	} = data?.data ?? {};

	const handleModalOk = async () => {
		const { input } = await form.validateFields();
		if (input) {
			try {
				const payload = {
					prospectId,
					rejectNotes: input?.rejectNotes,
					rejectCategoryId: input?.rejectCategoryId,
					photo: input?.photo,
				};
				await rejectMutation.mutateAsync(payload);
				setShowModal(false);
				notification.success({
					message: 'Success',
					description:
						'Laporan Kunjungan berhasil di reject, hubungi FBDH untuk approval',
				});
				navigate('FRMVisitReportListPage');
			} catch (error) {
				if (fetcher.isError(error)) {
					const message = await error.json();
					notification.error({
						duration: 20,
						message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
						description: (
							<div>
								{message?.metadata?.debugInfo?.messages &&
								message?.metadata?.debugInfo?.messages.length ? (
									<ul>
										{message?.metadata?.debugInfo?.messages.map(
											(msg: string) => <li>{msg}</li>,
										)}
									</ul>
								) : (
									<pre>Opps, Something went wrong</pre>
								)}
							</div>
						),
					});
				}
			}
			// TODO: submit mutation
			// handleSubmitReject(values);
		}
	};

	if (isError) return <GenericErrorMessage />;
	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				isLoading={isLoading}
			/>

			<Divider />
			<FleetTitle>Approval History</FleetTitle>
			<Table
				dataSource={
					approvalHistories?.map((record, key) => {
						return { ...record, key };
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						dataIndex: 'no',
					},
					{
						title: 'Name',
						dataIndex: 'approvalBy',
					},
					{
						title: 'Date',
						dataIndex: 'approvalDate',
					},
					{
						title: 'Process',
						dataIndex: 'approvalProcess',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.approvalStatus.toLowerCase()}`}
								>
									{record.approvalStatus}
								</span>
							);
						},
					},
					{
						title: 'Approval Notes',
						dataIndex: 'approvalNote',
					},
				]}
				bordered
				pagination={false}
				style={{ marginBottom: 20 }}
			/>
			<FleetTitle>Reject Notes</FleetTitle>
			<Form.Provider>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onSubmitCapture={async () => {
						const isFormValid = await form.validateFields();
						if (isFormValid) {
							setShowModal(true);
						}
					}}
				>
					<Form.Item shouldUpdate>
						{({ setFieldValue }) => {
							return (
								<Space {...ANTD_DEFAULT_PROPS.SPACE}>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'rejectCategoryId'])}
												label="Category Reject"
												labelCol={{ span: 6 }}
												rules={[rule.required()]}
												required
											>
												<Select {...selectRejectCategory} />
											</Form.Item>
										</Col>
										<Col span={12}></Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'photo'])}
												noStyle
											/>
											<Form.Item
												label="Upload Foto"
												name={fieldName(['input', 'photoData'])}
												labelCol={{ span: 6 }}
												required
												rules={[rule.required()]}
												valuePropName="fileList"
												getValueFromEvent={(e) =>
													Array.isArray(e) ? e : e?.fileList
												}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													beforeUpload={async (file) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${prospectId}/reject-visit-report`;

														try {
															const url = await uploadMutation.mutateAsync({
																file,
																fileid,
																filepath,
															});

															setFieldValue(fieldName(['input', 'photo']), {
																format: 'jpeg',
																id: fileid,
																name: file.name,
																path: filepath,
																url,
															} as FilePayload);
														} catch {
															setFieldValue(
																fieldName(['input', 'photo']),
																null,
															);
														}

														return false;
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
										<Col span={24}>
											<Form.Item
												name={['input', 'rejectNotes']}
												label="Reject Notes"
												labelCol={{ span: 3 }}
											>
												<Input.TextArea
													autoSize={{ minRows: 3 }}
													style={{ marginLeft: '-5px' }}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Flex
										justify="end"
										gap={20}
									>
										<Button
											style={{
												width: 200,
												display: 'block',
												marginTop: 60,
											}}
											onClick={() => {
												navigate('FRMVisitReportListPage');
											}}
										>
											BACK
										</Button>
										<Button
											type="primary"
											htmlType="submit"
											loading={isLoading}
											style={{
												width: 200,
												display: 'block',
												marginTop: 60,
											}}
										>
											SAVE
										</Button>
									</Flex>
								</Space>
							);
						}}
					</Form.Item>
				</Form>
			</Form.Provider>

			<FleetModal
				open={showModal}
				styles={{ footer: { padding: 0 } }}
				okText="NEXT"
				cancelText="CANCEL"
				onCancel={() => setShowModal(false)}
				onOk={handleModalOk}
			>
				<Result
					subTitle={
						<p style={{ color: '#2F2F2F' }}>
							Apakah Anda yakin ingin <b>REJECT</b> laporan kunjungan ini?
						</p>
					}
				></Result>
			</FleetModal>
		</Fragment>
	);
};

export default FRMVisitReportRejectPage;
